import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import AddQuizDialog from "./AddQuizDialog";
import ConfirmationDialog from "components/Dialog/ConfirmationDialog";
import editIcon from "../../../../assets/images/add-course/edit.svg";
import { getSignedUrl } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";

// Small reusable image preview component for quiz questions/options
const QuizPreviewImage = ({ fileName, effectiveCourseId, activeSection }) => {
  const [url, setUrl] = useState(null);
  const [cookies] = useCookies(["userId"]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const payload = {
          courseId: effectiveCourseId,
          sectionId: activeSection?.sectionId,
          fileName,
          method: "GET",
          userId: cookies.userId,
        };
        const response = await getSignedUrl(payload);
        if (response.data?.preSignedUrl) {
          setUrl(response.data.preSignedUrl);
        }
      } catch (err) {
        console.error("Failed to load quiz image preview", err);
      }
    };
    if (fileName && effectiveCourseId && activeSection?.sectionId) {
      if (fileName.startsWith("blob:") || fileName.startsWith("http")) {
        setUrl(fileName);
      } else {
        fetchUrl();
      }
    }
  }, [fileName, effectiveCourseId, activeSection, cookies.userId]);

  if (!url) return <span className="text-muted small">Loading image...</span>;

  return (
    <>
      <img
        src={url}
        alt={fileName}
        onClick={() => setIsFullscreen(true)}
        style={{
          maxHeight: "80px",
          maxWidth: "120px",
          borderRadius: "6px",
          objectFit: "contain",
          border: "1px solid #dee2e6",
          cursor: "pointer",
          transition: "transform 0.2s",
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
      />

      <Modal show={isFullscreen} onHide={() => setIsFullscreen(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate">{fileName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center align-items-center bg-dark p-0">
          <img
            src={url}
            alt={fileName}
            style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

const ShowQuizes = ({
  quiz,
  removeQuiz,
  updateQuizQuestion,
  isEditMode,
  activeQuiz,
  effectiveCourseId, // Added to receive from parent
  activeSection, // Added to receive from parent
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState({});
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [openConfrimationDialog, setOpenConfrimationDialog] = useState(false);
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleQuizSubmit = () => {};
  const closeConfrimationDialog = () => {
    setOpenConfrimationDialog(false);
  };

  const handleConfirm = () => {
    const payload = {
      sectionNumber: "",
      contentNumber: "",
      questionId: currentQuestionId,
    };
    removeQuiz(payload);
    closeConfrimationDialog();
  };

  // Sync image deletions immediately to the quiz state so the accordion
  // never shows stale/broken image references, even if the modal is closed
  // without clicking "Edit Quiz".
  const handleImageDeleteSync = (questionId, targetType, optionIndex, updatedImageNames) => {
    if (!quiz?.questions) return;
    const updatedQuestions = quiz.questions.map(q => {
      if (q.id !== questionId) return q;
      if (targetType === "question") {
        return { ...q, imageQuestion: updatedImageNames };
      } else if (targetType === "option" && q.answers) {
        const updatedAnswers = [...q.answers];
        if (updatedAnswers[optionIndex]) {
          updatedAnswers[optionIndex] = {
            ...updatedAnswers[optionIndex],
            imageOptionName: updatedImageNames,
          };
        }
        return { ...q, answers: updatedAnswers };
      }
      return q;
    });
    // Directly mutate the quiz prop's questions array to reflect in parent
    quiz.questions = updatedQuestions;
  };

  return (
    <>
      <div>
        {quiz?.questions?.length > 0 &&
          quiz?.questions?.map((question, index) => {
            return (
              <>
                <div className="mt-4">
                  <div className="d-flex justify-content-between p-0">
                    <span>Question {index + 1}</span>
                    {isEditMode && (
                      <>
                        <div>
                          <img
                            className="cursor-pointer"
                            src={editIcon}
                            alt="edit icon"
                            onClick={() => {
                              setCurrentQuiz(question);
                              handleOpenModal();
                            }}
                          />
                          {/* <img
                        src={deleteIcon}
                        alt="delete icon"
                        className="cursor-pointer mx-2"
                        onClick={() => {
                          setOpenConfrimationDialog(true);
                          setCurrentQuestionId(question?.id);
                        }}
                      /> */}
                        </div>
                      </>
                    )}
                  </div>
                  <Accordion className="px-0 mt-2">
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <div className=""> {question.question}</div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="d-flex flex-column" style={{ gap: '12px' }}>
                        {/* Question Image(s) */}
                        {question?.imageQuestion?.length > 0 && (
                          <div className="d-flex flex-wrap gap-2">
                            {question.imageQuestion.map((imgName, imgIdx) => (
                              <QuizPreviewImage
                                key={imgIdx}
                                fileName={imgName}
                                effectiveCourseId={effectiveCourseId}
                                activeSection={activeSection}
                              />
                            ))}
                          </div>
                        )}

                        {question?.answers && question.answers.length > 0 ? (
                          <div>
                            <span className="fw-bold">Options:</span>
                            <ul className="mb-0 mt-1">
                              {question.answers.map((option, idx) => {
                                if (option?.answer !== "") {
                                  return (
                                    <li
                                      key={idx}
                                      className={`${question?.correctAnswer === idx + 1 ? "text-success fw-bold" : ""}`}
                                    >
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span>{option?.answer}</span>
                                        {/* Option Image(s) */}
                                        {option?.imageOptionName?.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1 ms-2">
                                            {option.imageOptionName.map((optImgName, optImgIdx) => (
                                              <QuizPreviewImage
                                                key={optImgIdx}
                                                fileName={optImgName}
                                                effectiveCourseId={effectiveCourseId}
                                                activeSection={activeSection}
                                              />
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  );
                                }
                                return null;
                              })}
                            </ul>
                          </div>
                        ) : (
                          question?.correctAnswer !== undefined && question?.correctAnswer !== "" ? (
                            <div>
                              <span className="fw-bold">Answer:</span>
                              <div className="text-success fw-bold mt-1">
                                {question.correctAnswer}
                              </div>
                            </div>
                          ) : null
                        )}

                        {question?.explanation && (
                          <div>
                            <span className="fw-bold">Explanation:</span>
                            <div className="text-muted mt-1">
                              {question.explanation}
                            </div>
                          </div>
                        )}

                        {question?.point !== undefined && question?.point !== "" && (
                          <div>
                            <span className="fw-bold">Marks:</span>
                            <div className="text-muted mt-1">
                              {question.point}
                            </div>
                          </div>
                        )}

                        {Array.isArray(question?.rubric) && question.rubric.length > 0 && (
                          <div>
                            <span className="fw-bold">Rubric:</span>
                            <div className="table-responsive mt-1">
                              <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.85em" }}>
                                <thead className="table-light">
                                  <tr>
                                    <th>Criterion</th>
                                    <th>Description</th>
                                    <th style={{ width: "70px" }}>Marks</th>
                                    <th>Partial Credit Guidelines</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {question.rubric.map((r, ridx) => (
                                    <tr key={ridx}>
                                      <td className="fw-semibold">{r.criterion}</td>
                                      <td>{r.description}</td>
                                      <td>{r.marks}</td>
                                      <td>{r.partial_credit_guidelines}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              </>
            );
          })}
      </div>

      {openModal ? (
        <AddQuizDialog
          show={openModal}
          editQuiz={true}
          quiz={currentQuiz}
          handleClose={handleClose}
          handleQuizSubmit={handleQuizSubmit}
          updateQuizQuestion={updateQuizQuestion}
          activeQuiz={activeQuiz}
          effectiveCourseId={effectiveCourseId}
          activeSection={activeSection}
          setAddQuestionBtnCLicked={() => {}}
          onImageDelete={handleImageDeleteSync}
        />
      ) : null}
      <ConfirmationDialog
        open={openConfrimationDialog}
        message="Are you sure, you want to delete?"
        handleClose={closeConfrimationDialog}
        handleConfirm={handleConfirm}
      />
    </>
  );
};

export default ShowQuizes;